import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, Row, Col } from 'antd';
import { Routes } from '@/routes';
import { useDevice, useDormitoryRequests, useValidEntranceDormitory } from '@/hooks';
import { Flex, PageContainer, NotAllowed, LoadingSpin, PageError } from '@/components';
import useDormitoryQualify from '@/hooks/app/dormitoryLegacy/useDormitoryQualify';
import DormitoryPreRegisterForm from './DormitoryPreRegisterForm';
import { dormitoryRequestStatusText } from '../helper';

function DormitoryPreRegister() {

  const navigate = useNavigate();
  const { isDesktop } = useDevice();
  const [t] = useTranslation(['common', 'dormitory']);

  const { fetchQualify, error: qualifyError, loading: qualifyLoading } = useDormitoryQualify(true);
  const { fetchDormitoryRequests, findRequestBySemesters, loading, error } = useDormitoryRequests();
  const { validEntrance, fetchValidEntrance, error: errorEntrance, loading: loadingEntrance } = useValidEntranceDormitory(true);


  const backText = t('common:labels.back');
  const pageTitle = t('dormitory:labels.pre-register');

  useEffect(() => {
    fetchDormitoryRequests();
    fetchValidEntrance();
    fetchQualify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || loadingEntrance || qualifyLoading) {
    return (
      <Flex align="center" className="h-full pb-20">
        <LoadingSpin />
      </Flex>
    );
  }

  if (qualifyError) return <PageError title={pageTitle} content={qualifyError} actionText={backText} action={() => navigate(Routes.Dormitory)} />;
  if (errorEntrance) return <PageError title={pageTitle} content={errorEntrance} actionText={backText} action={() => navigate(Routes.Dormitory)} />;
  if (error) return <PageError title={pageTitle} content={error} action={fetchDormitoryRequests} />;
  if (!validEntrance?.inPreregistrationPeriod) return <PageError title={pageTitle} content={t('dormitory:errors.not-in-pre-registeration-period')} actionText={backText} action={() => navigate(Routes.Dormitory)} />;
  if (!validEntrance?.preregistrationEnabled) return <PageError title={pageTitle} content={t('dormitory:errors.pre-register-not-active-for-you')} actionText={backText} action={() => navigate(Routes.Dormitory)} />;

  const validRequest = findRequestBySemesters(validEntrance?.semesters);

  return (
    <PageContainer title={pageTitle}>
      <ConfigProvider componentSize={isDesktop() ? 'large' : 'middle'}>
        <Flex direction="column" align="center" className="w-full h-full pb-20">
          <Row className="w-full pt-4" justify="center">
            <Col xxl={8} xl={10} lg={12} md={14} xs={24} span={24}>
              {
                validRequest ? (
                  <NotAllowed type="warning">
                    <Flex direction="column">
                      <span className="mb-1">{t('dormitory:errors.pre-register-already-done')}</span>
                      <Flex direction="column" gap="0.5rem">
                        <Flex>
                          <div className="w-full">{t('dormitory:labels.semester')}:</div>
                          <Flex className="w-full">
                            <span>{validRequest?.semester?.period?.name}</span>
                            <span>{validRequest?.semester?.name}</span>
                          </Flex>
                        </Flex>
                        <Flex>
                          <span className="w-full">{t('dormitory:labels.dormitory-requested')}: </span>
                          <span className="w-full">{validRequest?.suggestedDormitory?.name ?? '-'}</span>
                        </Flex>
                        <Flex>
                          <span className="w-full">{t('dormitory:labels.dormitory-destination')}: </span>
                          <span className="w-full">{validRequest?.destinationDormitory?.name ?? '-'}</span>
                        </Flex>
                        <Flex>
                          <span className="w-full">{t('common:labels.status')}: </span>
                          <span className="w-full">{dormitoryRequestStatusText(validRequest?.status, t)}</span>
                        </Flex>
                      </Flex>
                    </Flex>
                  </NotAllowed>
                ) :
                  !validEntrance?.preRegEnabled ?
                    (<NotAllowed type="warning">
                      <span>{t('dormitory:errors.pre-register-not-allowed')}</span>
                    </NotAllowed>)
                    :
                    <DormitoryPreRegisterForm validEntrance={validEntrance} />
              }
            </Col>
          </Row>
        </Flex>
      </ConfigProvider>
    </PageContainer>
  );
}

export default DormitoryPreRegister;
